import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, throwError, of } from 'rxjs';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { catchError, switchMap } from 'rxjs/operators';
import { MessagingVMs } from '@cleverly/shared/data';
import { AuthService } from '@cleverly/shared/api-authorization';
import { BackendService } from '@cleverly/shared/core';

@Injectable({
    providedIn: 'root',
})
export class UserdeviceService {
    constructor(
        private http: BackendService,
        private authService: AuthService
    ) {}

    private async getDeviceInfo() {
        try {
            return await Device.getId();
        } catch {
            return { identifier: 'web-' + Math.random().toString(36).substring(2, 15) };
        }
    }

    private async getDeviceDetails() {
        try {
            return await Device.getInfo();
        } catch {
            return {
                name: 'Web Browser',
                platform: 'web',
                operatingSystem: navigator.platform,
                osVersion: navigator.userAgent,
                manufacturer: navigator.vendor,
                isVirtual: false,
                webViewVersion: navigator.appVersion,
            };
        }
    }

    private async getAppInfo() {
        try {
            return await App.getInfo();
        } catch {
            return {
                version: '1.0.0',
                build: 'web',
                name: 'Cleverly Web',
                id: 'web.cleverly.app',
            };
        }
    }

    public SaveUserDevice(fcmTokenParam: string): Observable<MessagingVMs.UserDeviceInfoVM> {
        return forkJoin({
            id: from(this.getDeviceInfo()),
            info: from(this.getDeviceDetails()),
            appInfo: from(this.getAppInfo()),
        }).pipe(
            switchMap((deviceInfo) => {
                const userDeviceInfoVM = <MessagingVMs.UserDeviceInfoVM>{
                    deviceInfo: JSON.stringify(deviceInfo.info),
                    uuid: deviceInfo.id.identifier,
                    fcmToken: fcmTokenParam,
                    appVersion: deviceInfo.appInfo.version,
                };

                return this.authService.accessToken$.pipe(
                    switchMap((token) => {
                        return this.http
                            .post<MessagingVMs.UserDeviceInfoVM>('/api/v1/UserDevice/SaveUserDevice', userDeviceInfoVM)
                            .pipe(catchError(this.handleError));
                    })
                );
            })
        );
    }

    public RemoveUserDevice(): Observable<MessagingVMs.UserDeviceInfoVM> {
        return forkJoin({
            id: from(this.getDeviceInfo()),
            info: from(this.getDeviceDetails()),
        }).pipe(
            switchMap((r) => {
                const userDeviceInfoVM = <MessagingVMs.UserDeviceInfoVM>{
                    deviceInfo: JSON.stringify(r.info),
                    uuid: r.id.identifier,
                };
                return this.http
                    .post<MessagingVMs.UserDeviceInfoVM>('/api/v1/UserDevice/RemoveUserDevice', userDeviceInfoVM)
                    .pipe(catchError(this.handleError));
            })
        );
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
        }
        return throwError(error.error);
    }
}
